import React, { useRef, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Space,
  Input,
  Menu,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchVendors, deleteVendor } from "../services/vendorsAPI";
import { useAuth } from "../hooks/useAuth";
import CreateVendorModal from "../components/CreateVendorModal";
import UpdateVendorModal from "../components/UpdateVendorModal";
import Highlighter from "react-highlight-words";
import { exportVendorsToPDF } from "../utils/pdfUtils";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";

const ProjectVendors = ({ selectedProject }) => {
  const projectId = selectedProject.project_id;
  const projectName = selectedProject.project_name;
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize('vendors'),
    total: 0,
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const {
    data: vendorData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "vendors",
      projectId,
      pagination.current,
      pagination.pageSize,
      columnFilters,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchVendors(
        projectId,
        pagination.current,
        pagination.pageSize,
        columnFilters,
        sortField,
        sortOrder
      ),
    enabled: !!projectId,
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVendor,
    onSuccess: () => {
      queryClient.invalidateQueries(["vendors", projectId]);
      notification.success({ message: "Vendor deleted successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      notification.error({
        message: "Failed to delete vendor",
        description: errorMessage,
      });
    },
  });

  const handleExportPDF = async () => {
    try {
      if (!vendorData || vendorData.vendors.length === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      const allVendorsData = await fetchVendors(
        projectId,
        1,
        vendorData.total,
        columnFilters,
        sortField,
        sortOrder
      );

      exportVendorsToPDF(allVendorsData.vendors, projectName, {
        preserveOrder: !!sortField,
      });
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    const nextSortField = sorter?.order ? sorter.columnKey : null;
    const nextSortOrder =
      sorter?.order === "ascend" ? "asc" : sorter?.order === "descend" ? "desc" : null;
    const isSortChanged = nextSortField !== sortField || nextSortOrder !== sortOrder;
    setSortField(nextSortField);
    setSortOrder(nextSortOrder);
    setPagination(isSortChanged ? { ...newPagination, current: 1 } : newPagination);
    savePreferredPageSize(newPagination.pageSize, 'vendors');
    const mappedFilters = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key][0]) {
        mappedFilters[key] = filters[key][0];
      }
    });
    setColumnFilters(mappedFilters);
  };

  if (isError) {
    notification.error({
      message: "Failed to load vendors",
      description: error.message,
    });
  }

  const handleUpdateClick = (vendor) => {
    setSelectedVendor(vendor);
    setIsUpdateModalVisible(true);
  };

  const handleDeleteClick = (vendorId) => {
    deleteMutation.mutate(vendorId);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    filteredValue: columnFilters[dataIndex] ? [columnFilters[dataIndex]] : null,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortField === "name" ? (sortOrder === "desc" ? "descend" : "ascend") : null,
      ...getColumnSearchProps("name"),
    },
    {
      title: "DBA",
      dataIndex: "dba",
      key: "dba",
      ...getColumnSearchProps("dba"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="1" onClick={() => handleUpdateClick(record)}>
                Update
              </Menu.Item>
              <Menu.Item key="2">
                <Popconfirm
                  title="Are you sure you want to delete this vendor?"
                  onConfirm={() => handleDeleteClick(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  Delete
                </Popconfirm>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Vendors</h1>
      <div className="flex gap-4 mb-4">
        <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
          Create
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleExportPDF}>
          Export PDF
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={vendorData?.vendors}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: vendorData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <CreateVendorModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />
      {selectedVendor && (
        <UpdateVendorModal
          visible={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          vendor={selectedVendor}
        />
      )}
    </div>
  );
};

const Vendors = () => {
  const { selectedProject } = useAuth();

  return (
    <ProjectVendors
      key={selectedProject.project_id}
      selectedProject={selectedProject}
    />
  );
};

export default Vendors;
